import React, { useEffect, useState } from 'react';
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval,
  isSameMonth, isToday, getDay, addMonths, subMonths, parseISO, isSameDay,
} from 'date-fns';
import { ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';
import { CalendarBlock } from '../../lib/types';
import { apiListCalendar, apiCreateCalendarBlock, apiDeleteCalendarBlock } from '../../lib/api';

const PROTECTION_COLORS = {
  hard: { bg: '#FEE2E2', border: '#FCA5A5', text: '#DC2626', label: 'Hard Protect' },
  soft: { bg: '#FEF3C7', border: '#FCD34D', text: '#D97706', label: 'Soft Protect' },
  open: { bg: '#D1FAE5', border: '#6EE7B7', text: '#059669', label: 'Open' },
};

/**
 * CalendarView — monthly calendar with protected time blocks.
 * Hard blocks (red) are immovable. Soft blocks (amber) can be overridden with reason.
 * Open blocks (green) are schedulable. Override-requested blocks show a badge.
 */
export default function CalendarView() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [blocks, setBlocks] = useState<CalendarBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  useEffect(() => {
    apiListCalendar()
      .then(setBlocks)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Pad to start week on Sunday
  const startPad = getDay(monthStart);
  const padDays = Array.from({ length: startPad });

  const getBlocksForDay = (date: Date) =>
    blocks.filter((b) => {
      try { return isSameDay(parseISO(b.date), date); } catch { return false; }
    });

  const handleDayClick = (date: Date) => {
    setSelectedDate(date);
    setShowAdd(true);
  };

  const addBlock = async (block: Partial<CalendarBlock>) => {
    const created = await apiCreateCalendarBlock(block);
    setBlocks((prev) => [...prev, created]);
    setShowAdd(false);
    setSelectedDate(null);
  };

  const deleteBlock = async (id: string) => {
    await apiDeleteCalendarBlock(id);
    setBlocks((prev) => prev.filter((b) => b.id !== id));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="h-6 w-6 rounded-full border-2 border-gray-200 border-t-gray-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="calendar-view">
      {/* Navigation */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
        <button
          onClick={() => setCurrentDate(subMonths(currentDate, 1))}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          data-testid="calendar-prev-month"
        >
          <ChevronLeft size={16} className="text-gray-600" />
        </button>
        <h2 className="text-sm font-bold text-gray-900">
          {format(currentDate, 'MMMM yyyy')}
        </h2>
        <button
          onClick={() => setCurrentDate(addMonths(currentDate, 1))}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          data-testid="calendar-next-month"
        >
          <ChevronRight size={16} className="text-gray-600" />
        </button>
      </div>

      {/* Legend */}
      <div className="flex gap-4 px-6 py-2 border-b border-gray-100 shrink-0">
        {Object.entries(PROTECTION_COLORS).map(([level, c]) => (
          <div key={level} className="flex items-center gap-1.5">
            <div
              className="w-3 h-3 rounded-full border"
              style={{ backgroundColor: c.bg, borderColor: c.border }}
            />
            <span className="text-[10px] text-gray-500 font-medium">{c.label}</span>
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="flex-1 overflow-auto p-4">
        {/* Day headers */}
        <div className="grid grid-cols-7 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} className="text-center text-[10px] font-bold text-gray-400 uppercase tracking-wider py-1">
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7 gap-1">
          {padDays.map((_, i) => (
            <div key={`pad-${i}`} className="h-24 rounded-lg" />
          ))}

          {days.map((day) => {
            const dayBlocks = getBlocksForDay(day);
            const hasOverride = dayBlocks.some((b) => b.override_requested);
            const isCurrentDay = isToday(day);

            return (
              <button
                key={day.toISOString()}
                onClick={() => handleDayClick(day)}
                className="h-24 rounded-lg border border-gray-100 hover:border-gray-300 p-1.5 text-left transition-all relative overflow-hidden"
                style={isCurrentDay ? { borderColor: '#3498DB', borderWidth: 2 } : {}}
                data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
              >
                <span
                  className={`text-xs font-bold block mb-1 ${
                    isCurrentDay ? 'text-blue-500' : isSameMonth(day, currentDate) ? 'text-gray-700' : 'text-gray-300'
                  }`}
                >
                  {format(day, 'd')}
                </span>

                {/* Override badge */}
                {hasOverride && (
                  <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-orange-400" />
                )}

                {/* Blocks */}
                <div className="space-y-0.5">
                  {dayBlocks.slice(0, 3).map((block) => {
                    const colors = PROTECTION_COLORS[block.protection_level] || PROTECTION_COLORS.open;
                    return (
                      <div
                        key={block.id}
                        className="text-[9px] font-medium px-1 rounded truncate"
                        style={{ backgroundColor: colors.bg, color: colors.text }}
                        data-testid={`calendar-block-${block.id}`}
                      >
                        {block.title}
                      </div>
                    );
                  })}
                  {dayBlocks.length > 3 && (
                    <div className="text-[9px] text-gray-400 font-medium px-1">
                      +{dayBlocks.length - 3} more
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Add block modal */}
      {showAdd && selectedDate && (
        <AddBlockModal
          date={selectedDate}
          onAdd={addBlock}
          onCancel={() => { setShowAdd(false); setSelectedDate(null); }}
          existingBlocks={getBlocksForDay(selectedDate)}
          onDelete={deleteBlock}
        />
      )}
    </div>
  );
}

// ── Add Block Modal ────────────────────────────────────────────────────────────

function AddBlockModal({
  date, onAdd, onCancel, existingBlocks, onDelete,
}: {
  date: Date; onAdd: (b: Partial<CalendarBlock>) => void; onCancel: () => void;
  existingBlocks: CalendarBlock[]; onDelete: (id: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [level, setLevel] = useState<'hard' | 'soft' | 'open'>('open');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20" data-testid="calendar-add-modal">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 w-full max-w-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900">
            {format(date, 'EEEE, MMMM d')}
          </h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600" data-testid="calendar-modal-close">
            <X size={16} />
          </button>
        </div>

        {/* Existing blocks */}
        {existingBlocks.length > 0 && (
          <div className="mb-4 space-y-1.5">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Existing</p>
            {existingBlocks.map((b) => {
              const c = PROTECTION_COLORS[b.protection_level] || PROTECTION_COLORS.open;
              return (
                <div key={b.id} className="flex items-center justify-between p-2 rounded-lg" style={{ backgroundColor: c.bg }}>
                  <span className="text-xs font-medium" style={{ color: c.text }}>{b.title}</span>
                  <button onClick={() => onDelete(b.id)} className="text-gray-400 hover:text-gray-600 ml-2">
                    <X size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="space-y-3">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Block title"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
            data-testid="calendar-block-title-input"
            autoFocus
          />
          <div className="grid grid-cols-3 gap-2">
            {(['open', 'soft', 'hard'] as const).map((l) => {
              const c = PROTECTION_COLORS[l];
              return (
                <button
                  key={l}
                  onClick={() => setLevel(l)}
                  className="rounded-lg border py-1.5 text-xs font-semibold transition-all"
                  style={level === l
                    ? { backgroundColor: c.bg, borderColor: c.border, color: c.text }
                    : { borderColor: '#e5e7eb', color: '#6b7280' }}
                  data-testid={`calendar-level-${l}`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button onClick={onCancel} className="flex-1 rounded-lg border border-gray-200 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={() => {
              if (!title.trim()) return;
              onAdd({ title: title.trim(), date: format(date, 'yyyy-MM-dd'), protection_level: level, override_requested: false });
            }}
            disabled={!title.trim()}
            className="flex-1 rounded-lg bg-gray-900 py-2 text-sm font-semibold text-white hover:bg-gray-700 disabled:opacity-40"
            data-testid="calendar-add-block-confirm"
          >
            Add Block
          </button>
        </div>
      </div>
    </div>
  );
}
